import React, { useEffect, useRef, useState } from "react";
import { createBlobUrl, cleanupBlobUrls } from "./sdkAssets";

// Define the props interface for the component
export interface LocalIdScanJourneyProps {
  backendUrl: string;
  token: string;
  journeyDefinitionId: string;
  defaultInputProvider?: string;
  tripleScanAnalyzerGuidancePage?: boolean;
  fileUploadOnCameraCaptureJourneys?: boolean;
  previewPages?: Record<string, any>;
  manualCapture?: boolean;
  assetsDirectory?: string;
  smartCapture?: Record<string, any>;
  isVerboseLogEnabled?: boolean;
  hideLogo?: boolean;
  hideAutoCaptureHints?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

// List of SDK script files that need to be loaded (based on actual files in your public directory)
const SDK_SCRIPTS = [
  // Vendor files (using actual names from your public directory)
  "vendor.axios.cee2297d8064255b0106.js",
  "vendor.asn1.js.3483cc7f63182a3f0b82.js",
  "vendor.bn.js.8949dfa435f84f8e13a5.js",
  "vendor.browserify-aes.bc1671aedb76de95cac6.js",
  "vendor.browserify-sign.262d500dac7fbd793b85.js",
  "vendor.buffer.f36bb1254e482f86b62a.js",
  "vendor.create-ecdh.69ce73dde28c4e953a9b.js",
  "vendor.diffie-hellman.96fa799e490efa568667.js",
  "vendor.elliptic.8350666830498dccd184.js",
  "vendor.hash.js.9a08305c13c240a0f2eb.js",
  "vendor.lottie-web.c63ca72f96d1c4837c16.js",
  "vendor.miller-rabin.dd407c301bc8de89c1bd.js",
  "vendor.mobile-device-detect.8de5473c1f1106923305.js",
  "vendor.mustache.0f5d8a4748abe782331b.js",
  "vendor.public-encrypt.deb04f7e80668dc1211e.js",
  "vendor.signalr-no-jquery.6ee4fa921ba8dc0eba79.js",
  "vendor.sjcl.f4547262815d41f5fc42.js",
  "vendor.stream-browserify.7ad9b49390911acdde5b.js",
  // IDES files
  "ides-micro.c7f56e19625c4d2863a9.js",
  // IDScan files (using actual names from your public directory)
  "idscan-jcs-10de8631.f74a686e9a7f6e42670c.js",
  "idscan-jcs-1ab2fc53.d7c35cd3b5fff8a83ca1.js",
  "idscan-jcs-3bb2ab8b.05fb7793762bf9f39d33.js",
  "idscan-jcs-43dd7041.2b3ac18c07ac038ba585.js",
  "idscan-jcs-6b882012.93839b50b8a7df33fef9.js",
  "idscan-jcs-76945664.16afcfbbc3f1014ec443.js",
  "idscan-jcs-77455583.178cfd426c47545c4f6f.js",
  "idscan-jcs-89419957.ffffb2cf10c5a8b1202d.js",
  "idscan-jcs-d91a9049.194314e02268c234efac.js",
];

// CSS files to load (you may need to add these if they exist)
const SDK_CSS: string[] = [
  // Add any CSS files if they exist in your public directory
];

// WASM files that need to be available
const SDK_WASM = ["idesmicro_asm.wasm", "idesmicro_asm.js"];

const LocalIdScanJourney: React.FC<LocalIdScanJourneyProps> = ({
  backendUrl,
  token,
  journeyDefinitionId,
  defaultInputProvider = "SMART_CAPTURE",
  tripleScanAnalyzerGuidancePage = true,
  fileUploadOnCameraCaptureJourneys = false,
  previewPages,
  manualCapture = false,
  assetsDirectory = "assets",
  smartCapture,
  isVerboseLogEnabled = false,
  hideLogo = false,
  hideAutoCaptureHints = false,
  className = "",
  style = {},
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Initialize the GBG SDK on component mount
  useEffect(() => {
    let idScanInstance: any = null;

    const initializeIdScanComponent = async () => {
      try {
        setIsLoading(true);
        setError(null);

        console.log("Loading SDK scripts:", SDK_SCRIPTS);
        console.log("Loading SDK CSS:", SDK_CSS);

        // Create a document script element for each SDK script
        const scriptPromises = SDK_SCRIPTS.map((scriptName) => {
          return new Promise<void>((resolve, reject) => {
            try {
              const script = document.createElement("script");
              const blobUrl = createBlobUrl(scriptName);

              script.src = blobUrl;
              script.async = true;
              script.onload = () => {
                console.log(`✓ Loaded script: ${scriptName}`);
                resolve();
              };
              script.onerror = (e) => {
                console.error(`✗ Failed to load script: ${scriptName}`, e);
                reject(
                  new Error(
                    `Failed to load script: ${scriptName}. Make sure the GBG SDK files are available in your public directory.`
                  )
                );
              };
              document.head.appendChild(script);
            } catch (err) {
              reject(err);
            }
          });
        });

        // Create a document link element for each CSS file
        const cssPromises = SDK_CSS.map((cssName) => {
          return new Promise<void>((resolve, reject) => {
            try {
              const link = document.createElement("link");
              const blobUrl = createBlobUrl(cssName);

              link.rel = "stylesheet";
              link.href = blobUrl;
              link.onload = () => {
                console.log(`✓ Loaded CSS: ${cssName}`);
                resolve();
              };
              link.onerror = (e) => {
                console.error(`✗ Failed to load CSS: ${cssName}`, e);
                reject(new Error(`Failed to load CSS: ${cssName}`));
              };
              document.head.appendChild(link);
            } catch (err) {
              reject(err);
            }
          });
        });

        // Log a helpful message about WASM files
        console.info(
          "Important: WebAssembly files must be available in the public directory of your application. " +
            "For WASM files to load correctly, ensure that the following files exist in your public directory: " +
            SDK_WASM.join(", ")
        );

        // Wait for all scripts and CSS to load
        await Promise.all([...scriptPromises, ...cssPromises]);

        console.log("All SDK files loaded successfully!");

        // Check if window.IDscan is available
        if (!window.IDscan) {
          throw new Error(
            "GBG SDK failed to initialize. Make sure WebAssembly files are available in your public directory: " +
              SDK_WASM.join(", ")
          );
        }

        // Initialize the GBG SDK
        const config = {
          backendUrl,
          container: containerRef.current,
          token,
          journeyDefinitionId,
          defaultInputProvider,
          tripleScanAnalyzerGuidancePage,
          fileUploadOnCameraCaptureJourneys,
          previewPages,
          manualCapture,
          assetsDirectory,
          smartCapture,
          isVerboseLogEnabled,
          hideLogo,
          hideAutoCaptureHints,
        };

        // Initialize the GBG SDK with our configuration
        idScanInstance = new window.IDscan.Component(config);

        setIsLoading(false);
      } catch (err) {
        console.error("Error initializing GBG SDK:", err);
        setError(
          err instanceof Error ? err.message : "Failed to initialize GBG SDK"
        );
        setIsLoading(false);
      }
    };

    // Initialize the component
    initializeIdScanComponent();

    // Clean up when component unmounts
    return () => {
      if (idScanInstance && typeof idScanInstance.destroy === "function") {
        idScanInstance.destroy();
      }
      cleanupBlobUrls();
    };
  }, [
    backendUrl,
    token,
    journeyDefinitionId,
    defaultInputProvider,
    tripleScanAnalyzerGuidancePage,
    fileUploadOnCameraCaptureJourneys,
    previewPages,
    manualCapture,
    assetsDirectory,
    smartCapture,
    isVerboseLogEnabled,
    hideLogo,
    hideAutoCaptureHints,
  ]);

  return (
    <div
      ref={containerRef}
      className={`gbg-id-scan-journey ${className}`}
      style={{
        minHeight: "500px",
        width: "100%",
        ...style,
      }}
    >
      {isLoading && (
        <div className="loading">Loading GBG ID Verification...</div>
      )}
      {error && (
        <div className="error" style={{ color: "red", padding: "20px" }}>
          <h3>Error Loading GBG SDK</h3>
          <p>{error}</p>
          <div>
            <h4>Troubleshooting Tips:</h4>
            <ul>
              <li>
                Ensure WebAssembly files are available in your public directory
              </li>
              <li>Check that your backend URL and token are correct</li>
              <li>Verify that you have a stable internet connection</li>
              <li>Check browser console for more detailed error messages</li>
              <li>
                Run:{" "}
                <code>
                  node node_modules/local-id-scan-journey/scripts/postinstall.js
                </code>{" "}
                to copy SDK files
              </li>
            </ul>
            <p>
              <strong>Note for Next.js users:</strong> Make sure you've copied
              the WASM files to your public directory. See the README for
              installation instructions.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocalIdScanJourney;

// Add TypeScript declaration for the window object
declare global {
  interface Window {
    IDscan: {
      Component: new (config: any) => any;
    };
  }
}
